import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import App from '@/App';
import { RequireAuth, RequireRole } from './guards';
import NotFound from './NotFound';
import Login from '@/modules/auth/Login';
import { useAuth } from '@/core/auth/useAuth';

// ── Lazy chunks per role ─────────────────────────────────────────────────
// Admins never download the field module (and its SQLite/sync engine).
// Operators never download the admin module (and its tables/charts).
const AdminApp = lazy(() => import('@/modules/admin'));
const FieldApp = lazy(() => import('@/modules/field'));
// El formulario público de preinstalación. Su propio chunk: lo abre gente que
// no tiene cuenta y que casi siempre llega por datos móviles, así que no debe
// arrastrar ni el panel ni el motor de sincronización de campo.
const PreinstalacionPublica = lazy(() => import('@/modules/public/preinstalacion'));

function RoleRedirect() {
  const { firebaseUser, user, loading } = useAuth();
  if (loading) return <Splash>Cargando…</Splash>;
  // Not authenticated at all → login
  if (!firebaseUser) return <Navigate to="/login" replace />;
  // Authenticated in Firebase, but Mongo User not resolved → wait on login
  // page (which now shows a "no provisionado" banner).
  if (!user) return <Navigate to="/login" replace />;
  return user.role === 'operator'
    ? <Navigate to="/field" replace />
    : <Navigate to="/admin" replace />;
}

function Splash({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full items-center justify-center p-8 text-content-subtle">
      {children}
    </div>
  );
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    children: [
      { index: true, element: <RoleRedirect /> },
      { path: 'login', element: <Login /> },
      {
        // **Fuera de `RequireAuth` a propósito**: el enlace es la credencial.
        // Es la única ruta del producto que se abre sin sesión, y el token lo
        // resuelve el backend contra la solicitud — nunca contra el usuario
        // que pudiera haber en esta pestaña.
        path: 'p/:token',
        element: (
          <Suspense fallback={<Splash>Cargando…</Splash>}>
            <PreinstalacionPublica />
          </Suspense>
        ),
      },
      {
        path: 'admin/*',
        element: (
          <RequireAuth>
            <RequireRole roles={['empresa', 'super_admin', 'admin']}>
              <Suspense fallback={<div className="p-8 text-content-subtle">Cargando panel…</div>}>
                <AdminApp />
              </Suspense>
            </RequireRole>
          </RequireAuth>
        ),
      },
      {
        path: 'field/*',
        element: (
          <RequireAuth>
            <RequireRole roles={['operator']}>
              <Suspense fallback={<div className="p-8 text-content-subtle">Cargando app de campo…</div>}>
                <FieldApp />
              </Suspense>
            </RequireRole>
          </RequireAuth>
        ),
      },
      // **404, no un redirect al inicio.** Mandar a `/` en silencio borraba de
      // la barra la dirección mal escrita, que es lo único que explica por qué
      // no funcionó — y dejaba a quien seguía un enlace roto convencido de que
      // el enlace estaba bien y el producto mal.
      //
      // Sin `RequireAuth` como el resto de esta rama: a quien no ha entrado, un
      // 404 le dice más que un salto al login por una URL que tampoco existe
      // con sesión.
      { path: '*', element: <NotFound /> },
    ],
  },
]);
